/**
 * DemoLoginPage — /demo-login[?next=/demo/lotus]
 *
 * The one place a demo account signs in with the username and password the
 * owner issued. A signed-in visitor sees the demos the account may open.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui';
import { asApiError, request } from '@/lib/api/http';
import './DemoLoginPage.css';

interface DemoMe {
  username: string;
  display_name: string;
  organization: string | null;
  demos: string[];
  agents: string[];
  expires_at: string | null;
}

const DEMOS: Array<{ slug: string; name: string; href: string; note: string }> = [
  { slug: 'dr-calorie', name: 'دکتر کالری', href: '/demo/dr-calorie-2/', note: 'منوی کامل با دستیار هوشمند و گفت‌وگوی صوتی' },
  { slug: 'lotus', name: 'کافه رستوران لوتوس', href: '/demo/lotus/', note: 'منو و دستیار صوتی در عمارت شکارچیان' },
];

/** Only same-site paths: the login page must not become an open redirect. */
function safeNext(value: string | null): string | null {
  return value && /^\/(?!\/)[\w\-./?=&%]*$/.test(value) ? value : null;
}

export default function DemoLoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [me, setMe] = useState<DemoMe | null>(null);
  const [checking, setChecking] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    request<DemoMe>('/demo-auth/me')
      .then((data) => live && setMe(data))
      .catch(() => undefined)
      .finally(() => live && setChecking(false));
    return () => {
      live = false;
    };
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const data = await request<DemoMe>('/demo-auth/login', { method: 'POST', body: { username: username.trim(), password } });
      setPassword('');
      if (next) window.location.assign(next);
      else setMe(data);
    } catch (failure) {
      setError(asApiError(failure).messageFa);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await request('/demo-auth/logout', { method: 'POST' }).catch(() => undefined);
    setMe(null);
  };

  const allowed = me ? DEMOS.filter((d) => me.demos.includes('*') || me.demos.includes(d.slug)) : [];

  return (
    <main className="gs-dl" id="main" dir="rtl" lang="fa">
      <div className="gs-dl-card">
        <Logo size={40} />
        {checking ? (
          <p className="gs-dl-lede" role="status">در حال بررسی…</p>
        ) : me ? (
          <>
            <h1>سلام {me.display_name}</h1>
            <p className="gs-dl-lede">با نام کاربری <bdi dir="ltr">{me.username}</bdi> وارد شده‌ای. یکی از دموها را باز کن:</p>
            <ul className="gs-dl-demos">
              {allowed.map((d) => (
                <li key={d.slug}>
                  <a href={d.href}>
                    <b>{d.name}</b>
                    <span>{d.note}</span>
                  </a>
                </li>
              ))}
              {allowed.length === 0 && <li className="gs-dl-empty">هنوز دموی فعالی برای این حساب تعریف نشده است.</li>}
            </ul>
            <Button variant="secondary" size="sm" onClick={() => void logout()}>خروج از حساب</Button>
          </>
        ) : (
          <form onSubmit={(event) => void submit(event)} className="gs-dl-form" aria-labelledby="dl-title">
            <h1 id="dl-title">ورود به دموهای گارسون</h1>
            <p className="gs-dl-lede">با نام کاربری و رمزی که گارسون برایت فرستاده وارد شو.</p>
            <label className="gs-dl-field">
              <span>نام کاربری</span>
              <input value={username} onChange={(e) => setUsername(e.target.value)} dir="ltr" autoComplete="username"
                autoCapitalize="none" autoCorrect="off" spellCheck={false} required />
            </label>
            <label className="gs-dl-field">
              <span>رمز عبور</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr"
                autoComplete="current-password" required />
            </label>
            {error && <p className="gs-dl-error" role="alert">{error}</p>}
            <Button type="submit" disabled={busy || !username.trim() || !password}>{busy ? 'در حال ورود…' : 'ورود'}</Button>
            <p className="gs-dl-hint">حساب نداری؟ از صفحهٔ اصلی گارسون درخواست دمو بده تا برایت بسازیم.</p>
          </form>
        )}
      </div>
    </main>
  );
}

/** Sends a visitor of a locked demo page to the login page (the API answered 401). */
export function DemoLoginRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const onLoginRequired = () => {
      if (!location.pathname.startsWith('/demo/')) return;
      navigate(`/demo-login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
    };
    window.addEventListener('garson:login-required', onLoginRequired);
    return () => window.removeEventListener('garson:login-required', onLoginRequired);
  }, [location.pathname, location.search, navigate]);
  return null;
}
